import { z } from "zod";

/**
 * The contract between the API and the storefront's cache. See
 * docs/architecture/storefront-sites.md, Revalidation.
 *
 * The storefront caches a restaurant's data under these tags. After a
 * change commits, the API posts the tags to the storefront's revalidation
 * route on its local address, with `STOREFRONT_REVALIDATE_SECRET` as the
 * bearer token.
 */

/** On the storefront's local address, such as `http://localhost:4110/internal/revalidate`. */
export const REVALIDATE_PATH = "/internal/revalidate";

/** `public.catalog.getMenu` data. */
export const menuCacheTag = (restaurantId: string) => `menu:${restaurantId}`;

/** `public.tenancy.getRestaurantByHost` data. */
export const restaurantCacheTag = (restaurantId: string) => `restaurant:${restaurantId}`;

/** `public.sites.getSite` data: the published website and its photos. */
export const siteCacheTag = (restaurantId: string) => `site:${restaurantId}`;

export const REVALIDATE_EVENTS = ["menu.changed", "restaurant.changed", "site.changed"] as const;
export type RevalidateEvent = (typeof REVALIDATE_EVENTS)[number];

/**
 * The JSON body. Every tag names the same restaurant as `restaurantId`, and
 * the storefront revalidates exactly these tags.
 */
export const revalidateRequestSchema = z
	.object({
		restaurantId: z.uuid(),
		event: z.enum(REVALIDATE_EVENTS),
		tags: z.array(z.string()).min(1).max(4),
	})
	.refine(
		(r) =>
			r.tags.every(
				(tag) =>
					tag === menuCacheTag(r.restaurantId) ||
					tag === restaurantCacheTag(r.restaurantId) ||
					tag === siteCacheTag(r.restaurantId),
			),
		{
			message: "Tags must be menu:, restaurant: or site:<restaurantId>",
			path: ["tags"],
		},
	);
export type RevalidateRequest = z.infer<typeof revalidateRequestSchema>;
